import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { ApplicationModes } from '@shared/models/enums/enums';
import { FeatureToggle } from '@shared/models/feature-toggle';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { HostPlatformService } from './host-platform.service';

@Injectable({ providedIn: 'root' })
export class LdFeatureFlagsService {
	constructor(private readonly shellQuery: ShellQuery) {}

	private get featureToggles$(): Observable<FeatureToggle[]> {
		return this.shellQuery.featureToggles$.pipe(filter((toggles): toggles is FeatureToggle[] => !!toggles));
	}

	/**
	 * Returns feature toggles from LaunchDarkly when running on a scanner,
	 * otherwise falls back to the store's featureToggles$ stream.
	 */
	getLDFeatureFlagsIfScanner(appMode: ApplicationModes): Observable<FeatureToggle[]> {
		if (HostPlatformService.isScanner(appMode)) {
			return this.fetchFromLD();
		}

		return this.featureToggles$;
	}

	private fetchFromLD(): Observable<FeatureToggle[]> {
		const apiEndpoint = this.shellQuery.apiEndpointFallback;

		if (!apiEndpoint) {
			return this.featureToggles$;
		}

		const ffUrl = `${apiEndpoint.replace(/\/RxEditor$/i, '')}/Web/GetRawFeatureToggle`;

		return from(fetch(ffUrl, { method: 'GET' })).pipe(
			switchMap(response => {
				if (response.ok && response.url === ffUrl) {
					return from(response.json() as Promise<FeatureToggle[]>);
				}

				return this.featureToggles$;
			}),
			catchError(() => this.featureToggles$)
		);
	}
}
